import {
  AddLocationAlt,
  PendingActions,
  HealthAndSafety,
  NoteAdd,
  Person,
  SyncAlt,
  Warehouse,
} from "@mui/icons-material";
import { useEffect } from "react";
import { Admin, CustomRoutes, Resource } from "react-admin";
import { Route, useNavigate } from "react-router-dom";
import { useAuth } from "../../hooks/useAuth";
import dataProvider from "../../lib/dataProvider";
import AceRouting from "./AceRouting";
import AwaitingCountersignature from "./AwaitingCountersignature";
import MyDownline from "./MyDownline";
import { ApiLogList, ApiLogShow } from "./ApiLog";
import {
  UnsignedContractRequestList,
  UnsignedContractRequestShow,
} from "./UnsignedContractRequest";
import {
  AddStateRequestEdit,
  AddStateRequestLabel,
  AddStateRequestList,
} from "./AddStateRequest";
import {
  UnsignedAddStateRequestList,
  UnsignedAddStateRequestShow,
} from "./UnsignedAddStateRequest";
import { AgencyCreate, AgencyEdit, AgencyList } from "./Agency";
import { AgentImport, AgentList, AgentShow, AgentEdit } from "./Agent";
import { CarrierCreate, CarrierEdit, CarrierList } from "./Carrier";
import {
  ContractRequestEdit,
  ContractRequestLabel,
  ContractRequestList,
} from "./ContractRequest";
import MyLayout from "./Layout";
import { UserCreate, UserEdit, UserList } from "./User";

const AgencyAdmin = () => {
  return (
    <Admin basename="/admin" layout={MyLayout} dataProvider={dataProvider}>
      <Resource
        name="contract-request"
        list={ContractRequestList}
        edit={ContractRequestEdit}
        icon={NoteAdd}
        options={{ label: ContractRequestLabel }}
      />
      <Resource
        name="add-state-request"
        list={AddStateRequestList}
        edit={AddStateRequestEdit}
        icon={AddLocationAlt}
        options={{ label: AddStateRequestLabel }}
      />
      <Resource
        name="agency"
        options={{ label: "PURL Agency Info" }}
        edit={AgencyEdit}
        list={AgencyList}
        icon={Warehouse}
        recordRepresentation={(record) => record.name}
      />
      {/* #7/#3 — agency admins (Principals) see the agents in their hierarchy.
          #9/#10 — they may edit an agent's LICENSED STATES only; re-assigning an
          agent's agency or editing identity stays Applied GA admin only,
          enforced by the backend and by AgentEdit locking those fields for
          non-Applied GA admins. */}
      <Resource
        name="agent"
        list={AgentList}
        show={AgentShow}
        edit={AgentEdit}
        icon={Person}
        recordRepresentation={(record) =>
          `${record.firstName} ${record.lastName} (${record.email})`
        }
      />
      <Resource
        name="carrier"
        list={CarrierList}
        edit={CarrierEdit}
        icon={HealthAndSafety}
      />
      <Resource
        name="user"
        create={UserCreate}
        edit={UserEdit}
        list={UserList}
        icon={Person}
        recordRepresentation={(record) =>
          `${record.firstName} ${record.lastName} (${record.email})`
        }
      />
      {/* #93 - the unfinished-signing queues, scoped server-side to the
          principal's own hierarchy; reminder sending included. */}
      <Resource
        name="unsigned-contract-request"
        list={UnsignedContractRequestList}
        show={UnsignedContractRequestShow}
        icon={PendingActions}
        options={{ label: "Unfinished DocuSigns" }}
      />
      <Resource
        name="unsigned-add-state-request"
        list={UnsignedAddStateRequestList}
        show={UnsignedAddStateRequestShow}
        icon={PendingActions}
        options={{ label: "Unfinished Add State" }}
      />
      <CustomRoutes>
        {/* #40 - principal reviews/signs their agency's held LOA/AOC requests. */}
        <Route path="/awaiting-countersignature" element={<AwaitingCountersignature />} />
        {/* #38 - principal's tiered downline (agencies + agents at every level). */}
        <Route path="/my-downline" element={<MyDownline />} />
      </CustomRoutes>
    </Admin>
  );
};

const FullAdmin = () => {
  return (
    <Admin basename="/admin" layout={MyLayout} dataProvider={dataProvider}>
      <Resource
        name="contract-request"
        list={ContractRequestList}
        edit={ContractRequestEdit}
        icon={NoteAdd}
        options={{ label: ContractRequestLabel }}
      />
      <Resource
        name="add-state-request"
        list={AddStateRequestList}
        edit={AddStateRequestEdit}
        icon={AddLocationAlt}
        options={{ label: AddStateRequestLabel }}
      />
      <Resource
        name="user"
        create={UserCreate}
        edit={UserEdit}
        list={UserList}
        icon={Person}
        recordRepresentation={(record) =>
          `${record.firstName} ${record.lastName} (${record.email})`
        }
      />
      <Resource
        name="agent"
        list={AgentList}
        show={AgentShow}
        edit={AgentEdit}
        icon={Person}
        recordRepresentation={(record) =>
          `${record.firstName} ${record.lastName} (${record.email})`
        }
      />
      <Resource
        options={{ label: "PURL Agency Info" }}
        name="agency"
        create={AgencyCreate}
        edit={AgencyEdit}
        list={AgencyList}
        icon={Warehouse}
        recordRepresentation={(record) => record.name}
      />
      <Resource
        name="carrier"
        list={CarrierList}
        create={CarrierCreate}
        edit={CarrierEdit}
        icon={HealthAndSafety}
      />
      {/* #12 — contract requests that started DocuSign signing but never
          finished (drop-off), with reminder sending. Applied GA admins only. */}
      <Resource
        name="unsigned-contract-request"
        list={UnsignedContractRequestList}
        show={UnsignedContractRequestShow}
        icon={PendingActions}
        options={{ label: "Unfinished DocuSigns" }}
      />
      <Resource
        name="unsigned-add-state-request"
        list={UnsignedAddStateRequestList}
        show={UnsignedAddStateRequestShow}
        icon={PendingActions}
        options={{ label: "Unfinished Add State" }}
      />
      {/* #17 — read-only outbound API call log, Applied GA admins only. */}
      <Resource
        name="api-log"
        list={ApiLogList}
        show={ApiLogShow}
        icon={SyncAlt}
        options={{ label: "API Logs" }}
      />
      <CustomRoutes>
        <Route path="/agent/import" element={<AgentImport />} />
        {/* #21 — read-only ACE routing rules, Applied GA dashboard only. */}
        <Route path="/ace-routing" element={<AceRouting />} />
      </CustomRoutes>
    </Admin>
  );
};

// #73 — Applied GA "Lite Admin" console. Internal AGA staff who get a trimmed set
// of resources: Contract Requests, Add State Requests, Users, Agents, PURL Agency
// Info, plus the two "Unfinished" queues. They can view + edit (no delete, hidden
// via canDelete in the shared views), and do NOT get Carriers, API Logs, ACE
// Routing, or Agent Import. Delete and role-escalation are also blocked server-side.
const LiteAdmin = () => {
  return (
    <Admin basename="/admin" layout={MyLayout} dataProvider={dataProvider}>
      <Resource
        name="contract-request"
        list={ContractRequestList}
        edit={ContractRequestEdit}
        icon={NoteAdd}
        options={{ label: ContractRequestLabel }}
      />
      <Resource
        name="add-state-request"
        list={AddStateRequestList}
        edit={AddStateRequestEdit}
        icon={AddLocationAlt}
        options={{ label: AddStateRequestLabel }}
      />
      <Resource
        name="user"
        create={UserCreate}
        edit={UserEdit}
        list={UserList}
        icon={Person}
        recordRepresentation={(record) =>
          `${record.firstName} ${record.lastName} (${record.email})`
        }
      />
      <Resource
        name="agent"
        list={AgentList}
        show={AgentShow}
        edit={AgentEdit}
        icon={Person}
        recordRepresentation={(record) =>
          `${record.firstName} ${record.lastName} (${record.email})`
        }
      />
      <Resource
        name="agency"
        options={{ label: "PURL Agency Info" }}
        edit={AgencyEdit}
        list={AgencyList}
        icon={Warehouse}
        recordRepresentation={(record) => record.name}
      />
      <Resource
        name="unsigned-contract-request"
        list={UnsignedContractRequestList}
        show={UnsignedContractRequestShow}
        icon={PendingActions}
        options={{ label: "Unfinished DocuSigns" }}
      />
      <Resource
        name="unsigned-add-state-request"
        list={UnsignedAddStateRequestList}
        show={UnsignedAddStateRequestShow}
        icon={PendingActions}
        options={{ label: "Unfinished Add State" }}
      />
    </Admin>
  );
};

const GarityStandard = () => {
  return (
    <Admin basename="/admin" layout={MyLayout} dataProvider={dataProvider}>
      <Resource
        name="contract-request"
        list={ContractRequestList}
        edit={ContractRequestEdit}
        icon={NoteAdd}
        options={{ label: ContractRequestLabel }}
      />
      <Resource
        name="add-state-request"
        list={AddStateRequestList}
        edit={AddStateRequestEdit}
        icon={AddLocationAlt}
        options={{ label: AddStateRequestLabel }}
      />
      <Resource
        name="user"
        list={UserList}
        edit={UserEdit}
        icon={Person}
        recordRepresentation={(record) =>
          `${record.firstName} ${record.lastName} (${record.email})`
        }
      />
      <Resource
        name="agent"
        list={AgentList}
        show={AgentShow}
        icon={Person}
        recordRepresentation={(record) =>
          `${record.firstName} ${record.lastName} (${record.email})`
        }
      />
      <Resource
        options={{ label: "PURL Agency Info" }}
        name="agency"
        list={AgencyList}
        edit={AgencyEdit}
        icon={Warehouse}
        recordRepresentation={(record) => record.name}
      />
      <Resource
        name="carrier"
        list={CarrierList}
        edit={CarrierEdit}
        icon={HealthAndSafety}
      />
    </Admin>
  );
};

const AdminComponent = () => {
  const {
    user,
    isStandardUser,
    isFullAdmin,
    isAgencyAdmin,
    isLiteAdmin,
    isStaff,
    loading,
  } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (loading) return;

    // Authentication is required.
    if (!user) {
      navigate("/login");
      return;
    }

    // Self-service agents are not staff and have no admin dashboard;
    // send them to their own dashboard (#29) instead of a blank admin shell.
    if (!isStaff(user)) {
      navigate("/");
    }
  }, [user, loading, navigate, isStaff]);

  if (loading) return <>Loading</>;

  if (!user) return <></>;

  if (!isStaff(user)) return <></>;

  if (isAgencyAdmin(user)) return <AgencyAdmin />;
  if (isFullAdmin(user)) return <FullAdmin />;
  if (isLiteAdmin(user)) return <LiteAdmin />;
  if (isStandardUser(user)) return <GarityStandard />;
};

export default AdminComponent;
